import React, { useMemo } from 'react';
import './style.scss';
import { Link } from '@sitecore-jss/sitecore-jss-react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

const Button = ({
  children,
  onClick = null,
  link = null,
  variant: fallbackVariant = 'contained',
  color: fallbackColor = 'primary',
  disabled = false,
  arrow: fallbackArrow,
  className: externalClassName = '',
  type = undefined,
  name = undefined,
  id = undefined,
  value = undefined,
  ignoreLinkClassName = false,
  fullWidth = false,
}) => {
  const { variant, color, arrow } = useMemo(() => {
    const linkClass = link?.value?.class;
    if (!ignoreLinkClassName && legitClasses.includes(linkClass)) {
      return getPropsFromClassName(linkClass);
    }
    return { variant: fallbackVariant, arrow: fallbackArrow, color: fallbackColor };
  }, [link, fallbackColor, fallbackArrow, fallbackVariant, ignoreLinkClassName]);

  const className = useMemo(() => {
    const hasArrow = arrow === 'left' || arrow === 'right';
    const simpleChildren = (() => {
      if (link && !children) {
        if (!children) return true;
      }
      return typeof children === 'string';
    })();

    return clsx(
      'button',
      `button-${variant}-${color}`,
      disabled && 'button-disabled',
      hasArrow && 'button-arrow',
      simpleChildren && 'button-text-simple-children',
      externalClassName,
      fullWidth && 'button-full-width'
    );
  }, [externalClassName, variant, arrow, color, disabled, fullWidth, children]);

  const Body = ({ children }) => (
    <>
      <Arrow visible={arrow === 'left'} />
      {children}
      <Arrow visible={arrow === 'right'} />
    </>
  );

  const content = typeof children === 'string' ? <div>{children}</div> : children;

  if (link) {
    const body = children ? content : <div>{link?.value?.text}</div>;
    if (disabled) {
      return (
        <button className={className}>
          <Body>{body}</Body>
        </button>
      );
    }
    return (
      <Link field={link} className={className} onClick={disabled ? null : onClick}>
        <Body>{body}</Body>
      </Link>
    );
  }

  return (
    <button
      className={className}
      onClick={disabled ? null : onClick}
      type={type}
      name={name}
      id={id}
      value={value}
    >
      <Body>{content}</Body>
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  onClick: PropTypes.func,
  link: PropTypes.object,
  color: PropTypes.oneOf(['primary', 'secondary']),
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  disabled: PropTypes.bool,
  arrow: PropTypes.oneOf(['left', 'right']),
  className: PropTypes.string,
  type: PropTypes.oneOf(['submit', undefined]),
  name: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ignoreLinkClassName: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

const Arrow = ({ visible = false }) => {
  if (!visible) return null;

  // noinspection HtmlUnknownTarget
  return (
    <div className="button-arrow-wrapper">
      <div className="button-arrow button-arrow-blue">
        <img src="/-/jssmedia/Project/FAB2/shared/images/button-arrow-blue.svg" />
      </div>
      <div className="button-arrow button-arrow-white">
        <img src="/-/jssmedia/Project/FAB2/shared/images/button-arrow-white.svg" />
      </div>
      <div className="button-arrow button-arrow-red">
        <img src="/-/jssmedia/Project/FAB2/shared/images/button-arrow-red.svg" />
      </div>
    </div>
  );
};

const legitClasses = [
  'button-contained-primary',
  'button-contained-primary-arrow-left',
  'button-contained-primary-arrow-right',
  'button-outline-primary',
  'button-outline-primary-arrow-left',
  'button-outline-primary-arrow-right',
  'button-text-primary',
  'button-text-primary-arrow-left',
  'button-text-primary-arrow-right',
  'button-contained-secondary',
  'button-contained-secondary-arrow-left',
  'button-contained-secondary-arrow-right',
  'button-outline-secondary',
  'button-outline-secondary-arrow-left',
  'button-outline-secondary-arrow-right',
  'button-text-secondary',
  'button-text-secondary-arrow-left',
  'button-text-secondary-arrow-right',
];

const getPropsFromClassName = (className) => {
  let variant = 'contained';
  let color = 'primary';
  let arrow = null;
  const has = (property) => className.indexOf(property) >= 0;
  if (has('outline')) variant = 'outlined';
  if (has('text')) variant = 'text';
  if (has('secondary')) color = 'secondary';
  if (has('arrow-right')) arrow = 'right';
  if (has('arrow-left')) arrow = 'left';
  return { variant, color, arrow };
};

export default Button;
